import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';

import { isEmpty } from 'lodash-es';
import Avatar from '@strava/ui/Avatar';
import clsx from '@strava/ui/clsx';
import {
  FOLLOW_ACTIONS,
  FOLLOWING_STATUS
} from 'constants/follows/following-constants';
import { followAthlete } from 'api/athlete';
import styles from './ContactEntry.scss';
import { t } from '../../../../../../../utils/invite-friends-utils';
import { useInviteFriendsContext } from '../../../../../../../state/InviteFriendsContext';
import {
  UPDATE_EMAILS,
  ADD_ALERT,
  CLEAR_ALERT
} from '../../../../../../../state/InviteFriends.actions';
import { extractEmailsFromString } from '../../../EmailTabContent.utils';

const ContactEntry = ({ contact }) => {
  const [config, setConfig] = useState({
    action: FOLLOW_ACTIONS.add,
    disabled: false
  });
  const [currentFollowingStatus, setCurrentFollowingStatus] = useState(
    contact.followingStatus
  );

  const [isLoading, setIsLoading] = useState(false);

  const { state, dispatch, track } = useInviteFriendsContext();
  const { emails, currentAthlete, invitedEmailsList } = state;

  useEffect(() => {
    let action;
    let disabled = false;
    if (contact.isOnStrava) {
      if (currentFollowingStatus === FOLLOWING_STATUS.pending) {
        action = FOLLOW_ACTIONS.followRequested;
        disabled = true;
      } else if (currentFollowingStatus === FOLLOWING_STATUS.following) {
        action = FOLLOW_ACTIONS.following;
        disabled = true;
      } else {
        action = contact.approveFollowers
          ? FOLLOW_ACTIONS.requestToFollow
          : FOLLOW_ACTIONS.follow;
      }
    } else {
      action = FOLLOW_ACTIONS.add;
      const { email } = contact;
      disabled =
        invitedEmailsList.includes(email) ||
        extractEmailsFromString(emails).includes(email);
    }
    setConfig((prev) => {
      return { ...prev, action, disabled };
    });
  }, [
    contact,
    contact.approveFollowers,
    contact.email,
    contact.isOnStrava,
    currentFollowingStatus,
    emails,
    invitedEmailsList
  ]);

  const disabledTextStyle = config.disabled ? styles.disabled : '';

  const handleBtnClick = (action) => {
    dispatch({ type: CLEAR_ALERT });
    track({
      element: `${action}_btn`,
      action: 'click',
      properties: {
        current_tab: 'email',
        athlete_id: contact.id
      }
    });

    switch (action) {
      case FOLLOW_ACTIONS.add: {
        const separator = isEmpty(emails) ? '' : ',';
        dispatch({
          type: UPDATE_EMAILS,
          payload: { emails: emails.concat(separator, contact.email) }
        });
        setConfig((prev) => {
          return { ...prev, disabled: true };
        });
        break;
      }

      case FOLLOW_ACTIONS.follow:
      case FOLLOW_ACTIONS.requestToFollow: {
        setIsLoading(true);
        followAthlete({
          followerId: currentAthlete.id,
          followingId: contact.id
        })
          .then((status) => {
            if (status === FOLLOWING_STATUS.error) {
              dispatch({
                type: ADD_ALERT,
                payload: {
                  alert: {
                    severity: 'error',
                    message: t(`modal.errors.generic`)
                  }
                }
              });
            } else {
              setCurrentFollowingStatus(status);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;
      }
      default: // noop
    }
  };

  return (
    <>
      <div className={styles.athleteMetadata}>
        <Avatar
          name={contact.name || ''}
          src={contact.picture || ''}
          type="athlete"
          size="small"
          badge={contact.badge || ''}
          alt=""
        />
        <div className={styles.nameAndEmail}>
          <h4 className={clsx(styles.name, disabledTextStyle)}>
            {contact.name}
          </h4>
          <p className={clsx(styles.email, disabledTextStyle)}>
            {contact.email}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="primary"
        className={styles.actionBtn}
        onClick={() => handleBtnClick(config.action)}
        disabled={config.disabled || isLoading}
      >
        {t(`modal.email.gmail_authorized.contact_cta.${config.action}`)}
      </Button>
    </>
  );
};

ContactEntry.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    picture: PropTypes.string,
    badge: PropTypes.string,
    email: PropTypes.string.isRequired,
    isOnStrava: PropTypes.bool.isRequired,
    followingStatus: PropTypes.oneOf([
      'following',
      'pending',
      'can_follow',
      'can_request'
    ]),
    approveFollowers: PropTypes.bool
  }).isRequired
};

export default ContactEntry;
